/**
 * Stockfish Engine Wrapper
 * Uses Stockfish 18 Lite WebAssembly for strong chess analysis in browser
 */

class StockfishEngine {
  constructor() {
    this.worker = null;
    this.isReady = false;
    this.pendingResolve = null;
    this.analysisCallback = null;
    this.depth = 15;
    this.skillLevel = 20; // 0-20, where 20 is strongest
  }

  async init() {
    if (this.worker && this.isReady) return;

    return new Promise((resolve, reject) => {
      try {
        // Load Stockfish from public folder
        this.worker = new Worker('/stockfish-lite.js');
        
        this.worker.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.worker.onerror = (error) => {
          console.error('Stockfish worker error:', error);
          reject(error);
        };

        // Wait for UCI initialization
        const initTimeout = setTimeout(() => {
          reject(new Error('Stockfish initialization timeout'));
        }, 15000);

        this.pendingResolve = () => {
          clearTimeout(initTimeout);
          this.isReady = true;
          // Set default options
          this.send('setoption name UCI_LimitStrength value true');
          this.send('setoption name Skill Level value ' + this.skillLevel);
          this.send('isready');
        };

        // Track readyok to complete init
        this._waitingForReady = resolve;
        
        this.send('uci');
      } catch (error) {
        console.error('Failed to initialize Stockfish:', error);
        reject(error);
      }
    });
  }

  send(command) {
    if (this.worker) {
      this.worker.postMessage(command);
    }
  }

  handleMessage(message) {
    // Handle UCI initialization
    if (message === 'uciok' && this.pendingResolve) {
      this.pendingResolve();
      this.pendingResolve = null;
    }
    
    if (message === 'readyok' && this._waitingForReady) {
      this._waitingForReady();
      this._waitingForReady = null;
    }

    // Handle analysis output
    if (this.analysisCallback) {
      if (message.startsWith('info depth')) {
        const info = this.parseInfo(message);
        if (info) {
          this.analysisCallback({ type: 'info', ...info });
        }
      } else if (message.startsWith('bestmove')) {
        const parts = message.split(' ');
        const bestMove = parts[1];
        const ponderMove = parts[3] || null;
        this.analysisCallback({ 
          type: 'bestmove', 
          bestMove, 
          ponderMove 
        });
        this.analysisCallback = null;
      }
    }
  }

  parseInfo(message) {
    const info = {};
    const parts = message.split(' ');
    
    for (let i = 0; i < parts.length; i++) {
      switch (parts[i]) {
        case 'depth':
          info.depth = parseInt(parts[i + 1]);
          break;
        case 'score':
          if (parts[i + 1] === 'cp') {
            info.score = parseInt(parts[i + 2]);
            info.scoreType = 'cp';
          } else if (parts[i + 1] === 'mate') {
            info.score = parseInt(parts[i + 2]);
            info.scoreType = 'mate';
          }
          break;
        case 'pv':
          info.pv = parts.slice(i + 1);
          break;
        case 'nodes':
          info.nodes = parseInt(parts[i + 1]);
          break;
        case 'nps':
          info.nps = parseInt(parts[i + 1]);
          break;
        default:
          break;
      }
    }
    
    return info.depth ? info : null;
  }

  /**
   * Set difficulty level (maps to Stockfish skill level)
   * @param {string} difficulty - 'easy', 'medium', 'hard', 'expert'
   */
  setDifficulty(difficulty) {
    const skillMap = {
      easy: 1,      // Very weak
      medium: 6,    // Club player level
      hard: 12,     // Strong amateur
      expert: 20    // Maximum strength
    };
    
    const depthMap = {
      easy: 5,
      medium: 10,
      hard: 15,
      expert: 18
    };

    this.skillLevel = skillMap[difficulty] || 10;
    this.depth = depthMap[difficulty] || 12;
    
    if (this.isReady) {
      this.send('setoption name Skill Level value ' + this.skillLevel);
    }
  }

  /**
   * Get the best move for a position
   * @param {string} fen - Position in FEN notation
   * @param {number} depth - Search depth (optional, uses difficulty default)
   * @returns {Promise<{bestMove: string, ponderMove: string, evaluation: object}>}
   */
  async getBestMove(fen, depth = null) {
    if (!this.isReady) {
      await this.init();
    }

    return new Promise((resolve) => {
      let lastInfo = null;
      const searchDepth = depth || this.depth;

      this.analysisCallback = (result) => {
        if (result.type === 'info') {
          lastInfo = result;
        } else if (result.type === 'bestmove') {
          resolve({
            bestMove: result.bestMove,
            ponderMove: result.ponderMove,
            evaluation: lastInfo
          });
        }
      };

      this.send('position fen ' + fen);
      this.send('go depth ' + searchDepth);
    });
  }

  /**
   * Analyze a position without making a move
   * @param {string} fen - Position in FEN notation  
   * @param {function} onProgress - Callback for analysis progress
   * @param {number} depth - Analysis depth
   * @returns {Promise<object>} - Final analysis result
   */
  async analyze(fen, onProgress = null, depth = 16) {
    if (!this.isReady) {
      await this.init();
    }

    return new Promise((resolve) => {
      let lastInfo = null;

      this.analysisCallback = (result) => {
        if (result.type === 'info') {
          lastInfo = result;
          if (onProgress) {
            onProgress(result);
          }
        } else if (result.type === 'bestmove') {
          resolve({
            bestMove: result.bestMove,
            ponderMove: result.ponderMove,
            ...lastInfo
          });
        }
      };

      // Use maximum skill for analysis
      this.send('setoption name Skill Level value 20');
      this.send('position fen ' + fen);
      this.send('go depth ' + depth);
    });
  }

  /**
   * Stop current analysis
   */
  stop() {
    this.send('stop');
  }

  /**
   * Reset the engine for a new game
   */
  newGame() {
    this.send('ucinewgame');
    this.send('isready');
  }

  /**
   * Terminate the worker
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.isReady = false;
    }
  }
}

// Singleton instance
let engineInstance = null;

export function getStockfishEngine() {
  if (!engineInstance) {
    engineInstance = new StockfishEngine();
  }
  return engineInstance;
}

export default StockfishEngine;
